<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

// Instagram API Client-ID und Zugriffstoken entfernt
const { t } = useI18n();
const photos = ref([]);
const loading = ref(true);
const error = ref(null);
const visibleCount = ref(9);
const loadMoreCount = ref(9);

// Lightbox-Zustand
const lightboxOpen = ref(false);
const currentPhotoIndex = ref(0);
const lightboxLoading = ref(false);
const zoomLevel = ref(1);
const isDragging = ref(false);
const dragStart = ref({ x: 0, y: 0 });
const imagePosition = ref({ x: 0, y: 0 });
const containerRect = ref(null);
const imageRect = ref(null);
const zoomSteps = [1, 1.5, 2, 2.5, 3];
const currentZoomStep = ref(0);
const mouseDownTime = ref(0);
const dragThreshold = 5; // Pixel-Schwelle für Drag-Erkennung
const dragStartPosition = ref({ x: 0, y: 0 });

// Site Settings vom App.vue importieren
const props = defineProps({
  siteSettings: {
    type: Object,
    required: true
  }
});

// Fotos laden
const fetchPhotos = async () => {
  loading.value = true;
  error.value = null;
  
  try {
    const response = await fetch('/api/photos.php');
    
    if (!response.ok) {
      throw new Error(`API Fehler: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.success && data.photos) {
      photos.value = data.photos.map(photo => ({
        id: photo.id,
        caption: photo.caption || 'HomeWard Band',
        imageUrl: photo.imageUrl,
        link: photo.imageUrl,
        date: new Date(photo.date)
      }));
    } else {
      throw new Error(data.error || 'Keine Fotos gefunden');
    }
  } catch (err) {
    console.error('Fehler beim Laden der Fotos:', err);
    error.value = 'Fotos konnten nicht geladen werden.';
  } finally {
    loading.value = false;
  }
};

// Mehr Fotos laden
const loadMore = () => {
  loadMoreCount.value += visibleCount.value;
};

// Lightbox öffnen
const openLightbox = (photo) => {
  const index = photos.value.findIndex(p => p.id === photo.id);
  if (index !== -1) {
    currentPhotoIndex.value = index;
    lightboxOpen.value = true;
    zoomLevel.value = 1;
    imagePosition.value = { x: 0, y: 0 };
  }
};

// Lightbox schließen
const closeLightbox = () => {
  lightboxOpen.value = false;
  zoomLevel.value = 1;
  imagePosition.value = { x: 0, y: 0 };
};

// Zum nächsten Foto navigieren
const nextPhoto = () => {
  lightboxLoading.value = true;
  currentPhotoIndex.value = (currentPhotoIndex.value + 1) % photos.value.length;
};

// Zum vorherigen Foto navigieren
const prevPhoto = () => {
  lightboxLoading.value = true;
  currentPhotoIndex.value = (currentPhotoIndex.value - 1 + photos.value.length) % photos.value.length;
};

// Bild geladen
const imageLoaded = () => {
  lightboxLoading.value = false;
};

// Tastaturnavigation hinzufügen
const handleKeyDown = (e) => {
  if (!lightboxOpen.value) return;
  
  switch (e.key) {
    case 'Escape':
      closeLightbox();
      break;
    case 'ArrowRight':
      nextPhoto();
      break;
    case 'ArrowLeft':
      prevPhoto();
      break;
  }
};

const startDrag = (event) => {
  // Nur linke Maustaste (button 0) erlauben
  if (event.button !== 0) return;
  
  if (zoomLevel.value > 1) {
    mouseDownTime.value = Date.now();
    dragStartPosition.value = { x: event.clientX, y: event.clientY };
    dragStart.value = {
      x: event.clientX - imagePosition.value.x,
      y: event.clientY - imagePosition.value.y
    };
    
    // Container und Bild-Dimensionen speichern
    const container = event.currentTarget;
    containerRect.value = container.getBoundingClientRect();
    const image = container.querySelector('img');
    imageRect.value = image.getBoundingClientRect();
  }
};

const onDrag = (event) => {
  // Nur wenn linke Maustaste gedrückt ist
  if (event.buttons !== 1) {
    endDrag();
    return;
  }
  
  if (zoomLevel.value > 1) {
    // Berechne die Distanz vom Startpunkt
    const dx = event.clientX - dragStartPosition.value.x;
    const dy = event.clientY - dragStartPosition.value.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // Wenn die Distanz den Schwellenwert überschreitet, starte das Ziehen
    if (distance > dragThreshold) {
      isDragging.value = true;
    }
    
    if (isDragging.value) {
      // Berechne die neue Position
      const newX = event.clientX - dragStart.value.x;
      const newY = event.clientY - dragStart.value.y;
      
      // Aktualisiere die Position
      imagePosition.value = {
        x: newX,
        y: newY
      };
      
      // Begrenze die Position
      constrainImagePosition();
    }
  }
};

const handleClick = (event) => {
  // Ignoriere den Click, wenn:
  // 1. Wir im Drag-Modus sind
  // 2. Die Maus zu lange gedrückt wurde (länger als 200ms)
  // 3. Die Maus sich zu weit bewegt hat
  if (isDragging.value || 
      Date.now() - mouseDownTime.value > 200 ||
      Math.sqrt(
        Math.pow(event.clientX - dragStartPosition.value.x, 2) +
        Math.pow(event.clientY - dragStartPosition.value.y, 2)
      ) > dragThreshold) {
    return;
  }
  
  const container = event.currentTarget;
  const rect = container.getBoundingClientRect();
  const mouseX = event.clientX - rect.left;
  const mouseY = event.clientY - rect.top;
  
  // Nächsten Zoom-Schritt berechnen
  currentZoomStep.value = (currentZoomStep.value + 1) % zoomSteps.length;
  const oldZoom = zoomLevel.value;
  zoomLevel.value = zoomSteps[currentZoomStep.value];
  
  // Wenn wir rauszoomen, Bild zentrieren
  if (zoomLevel.value === 1) {
    imagePosition.value = { x: 0, y: 0 };
    return;
  }
  
  // Zoom-Punkt berechnen
  const scale = zoomLevel.value / oldZoom;
  
  // Neue Position berechnen
  imagePosition.value = {
    x: mouseX - (mouseX - imagePosition.value.x) * scale,
    y: mouseY - (mouseY - imagePosition.value.y) * scale
  };
  
  constrainImagePosition();
};

const handleWheel = (event) => {
  event.preventDefault();
  const delta = event.deltaY;
  
  // Finde den aktuellen Zoom-Schritt
  const currentIndex = zoomSteps.indexOf(zoomLevel.value);
  let newIndex;
  
  if (delta < 0) {
    // Zoom in
    newIndex = Math.min(currentIndex + 1, zoomSteps.length - 1);
  } else {
    // Zoom out
    newIndex = Math.max(currentIndex - 1, 0);
  }
  
  const oldZoom = zoomLevel.value;
  zoomLevel.value = zoomSteps[newIndex];
  currentZoomStep.value = newIndex;
  
  // Wenn wir rauszoomen, Bild zentrieren
  if (zoomLevel.value === 1) {
    imagePosition.value = { x: 0, y: 0 };
    return;
  }
  
  // Zoom-Punkt berechnen
  const container = event.currentTarget;
  const rect = container.getBoundingClientRect();
  const mouseX = event.clientX - rect.left;
  const mouseY = event.clientY - rect.top;
  
  // Zoom-Faktor berechnen
  const scale = zoomLevel.value / oldZoom;
  
  // Neue Position berechnen
  imagePosition.value = {
    x: mouseX - (mouseX - imagePosition.value.x) * scale,
    y: mouseY - (mouseY - imagePosition.value.y) * scale
  };
  
  constrainImagePosition();
};

const constrainImagePosition = () => {
  if (!containerRect.value || !imageRect.value) return;
  
  const container = containerRect.value;
  const image = imageRect.value;
  
  // Berechne die tatsächlichen Dimensionen des gezoomten Bildes
  const scaledWidth = image.width * zoomLevel.value;
  const scaledHeight = image.height * zoomLevel.value;
  
  // Berechne die maximalen Verschiebungen
  const maxX = Math.max(0, (scaledWidth - container.width) / 2);
  const maxY = Math.max(0, (scaledHeight - container.height) / 2);
  
  // Wenn das Bild kleiner als der Container ist, zentriere es
  if (scaledWidth <= container.width) {
    imagePosition.value.x = 0;
  } else {
    imagePosition.value.x = Math.max(-maxX, Math.min(maxX, imagePosition.value.x));
  }
  
  if (scaledHeight <= container.height) {
    imagePosition.value.y = 0;
  } else {
    imagePosition.value.y = Math.max(-maxY, Math.min(maxY, imagePosition.value.y));
  }
};

const endDrag = () => {
  isDragging.value = false;
  containerRect.value = null;
  imageRect.value = null;
  mouseDownTime.value = 0;
};

// Fotos beim Start laden und Keydown-Event hinzufügen
onMounted(() => {
  fetchPhotos();
  window.addEventListener('keydown', handleKeyDown);
});
</script>

<template>
  <section id="photos" class="photos">
    <div class="container">
      <div class="section-header">
        <h2>Fotos</h2>
        <div class="celtic-divider">
          <span></span>
          <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
            <path d="M12,2C9.1,2,6.8,4.3,6.8,7.2c0,1.7,0.8,3.3,2.1,4.3c-1.3,1-2.1,2.5-2.1,4.3c0,1.7,0.8,3.3,2.1,4.3C7.6,21,6.8,22.5,6.8,24h2.4c0-1.3,1.1-2.4,2.4-2.4c1.3,0,2.4,1.1,2.4,2.4h2.4c0-1.5-0.8-3-2.1-3.9c1.3-1,2.1-2.5,2.1-4.3c0-1.7-0.8-3.3-2.1-4.3c1.3-1,2.1-2.5,2.1-4.3C16.8,4.3,14.9,2,12,2z" />
          </svg>
          <span></span>
        </div>
      </div>

      <!-- Loading Indicator -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Fotos werden geladen...</p>
      </div>
      
      <!-- Error Message -->
      <div v-else-if="error" class="error-state">
        <p>{{ error }}</p>
        <button @click="fetchPhotos" class="btn-retry">Erneut versuchen</button>
      </div>

      <!-- No Photos -->
      <div v-else-if="photos.length === 0" class="empty-state">
        <p>Aktuell sind keine Fotos verfügbar.</p>
      </div>

      <!-- Photos Grid -->
      <div v-else class="photos-grid">
        <div 
          v-for="(photo, index) in photos.slice(0, visibleCount)" 
          :key="photo.id" 
          class="photo-item"
        >
          <div @click="openLightbox(photo)" class="photo-link">
            <div class="photo-image">
              <img :src="photo.imageUrl" :alt="photo.caption" loading="lazy">
            </div>
            <div class="photo-info">
              <p class="photo-caption">{{ photo.caption }}</p>
              <p class="photo-date">{{ photo.date.toLocaleDateString('de-DE', { year: 'numeric', month: 'short', day: 'numeric' }) }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Load More Button -->
      <div v-if="photos.length > visibleCount" class="load-more">
        <button @click="loadMore" class="btn-load-more">
          Mehr Fotos laden
        </button>
      </div>

      <!-- Lightbox -->
      <div v-if="lightboxOpen" class="lightbox" @click="closeLightbox">
        <div class="lightbox-content" @click.stop>
          <button class="lightbox-close" @click="closeLightbox">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
          
          <div class="lightbox-nav lightbox-prev" @click="prevPhoto">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </div>
          
          <div class="lightbox-nav lightbox-next" @click="nextPhoto">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </div>
          
          <div class="lightbox-image-container"
               @wheel="handleWheel"
               @click="handleClick"
               @mousedown="startDrag"
               @mousemove="onDrag"
               @mouseup="endDrag"
               @mouseleave="endDrag">
            <div v-if="lightboxLoading" class="lightbox-loading">
              <div class="spinner"></div>
            </div>
            <img 
              :src="photos[currentPhotoIndex].imageUrl" 
              :alt="photos[currentPhotoIndex].caption"
              @load="imageLoaded"
              class="lightbox-image"
              :style="{
                transform: `scale(${zoomLevel}) translate(${imagePosition.x}px, ${imagePosition.y}px)`,
                cursor: isDragging ? 'grabbing' : (zoomLevel > 1 ? 'grab' : 'zoom-in')
              }"
            >
          </div>
          
          <div class="lightbox-caption">
            <h3>{{ photos[currentPhotoIndex].caption }}</h3>
            <p>{{ photos[currentPhotoIndex].date.toLocaleDateString('de-DE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) }}</p>
          </div>
        </div>
      </div>

      <div class="instagram-cta">
        <h3>{{ $t('sections.photos.follow_instagram') }}</h3>
        <p>{{ $t('sections.photos.follow_instagram_text') }}</p>
        <a :href="props.siteSettings.instagram_url || '#'" target="_blank" class="btn btn-primary" :class="{ 'disabled': !props.siteSettings.instagram_url }">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
          </svg>
          {{ $t('sections.photos.follow_instagram_button') }}
        </a>
      </div>
    </div>
  </section>
</template>

<style scoped>
.photos {
  padding: 60px 0;
  background-color: var(--bg-alt);
}

.section-header {
  text-align: center;
  margin-bottom: 40px;
}

.section-header h2 {
  font-size: 2.5rem;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
  font-family: var(--font-family-serif);
  font-weight: 600;
}

.celtic-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);
  margin: 1rem auto;
}

.celtic-divider span {
  height: 1px;
  width: 100px;
  background-color: var(--primary-color);
  opacity: 0.5;
}

.celtic-divider svg {
  margin: 0 1rem;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.photos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.photo-item {
  background-color: var(--white);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  cursor: pointer;
}

.photo-item:hover {
  transform: translateY(-5px);
}

.photo-link {
  display: block;
  text-decoration: none;
  color: inherit;
}

.photo-image {
  position: relative;
  padding-bottom: 100%; /* Square ratio */
  overflow: hidden;
}

.photo-image img {
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.photo-item:hover .photo-image img {
  transform: scale(1.05);
}

.photo-info {
  padding: 15px;
}

.photo-caption {
  font-weight: 500;
  margin: 0 0 5px 0;
  color: var(--text-color);
}

.photo-date {
  font-size: 0.9rem;
  color: var(--text-muted);
  margin: 0;
}

.empty-state, .error-state, .loading-state {
  text-align: center;
  padding: 60px 20px;
  background-color: var(--white);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 132, 61, 0.2);
  border-radius: 50%;
  border-top-color: var(--primary-color);
  animation: spin 1s infinite linear;
  margin: 0 auto 20px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.btn-retry {
  margin-top: 20px;
  background-color: var(--primary-color);
  color: var(--white);
  border: none;
  border-radius: 5px;
  padding: 10px 20px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s;
}

.btn-retry:hover {
  background-color: var(--primary-dark);
}

.load-more {
  display: flex;
  justify-content: center;
  margin-top: 40px;
}

.btn-load-more {
  background-color: transparent;
  border: 2px solid var(--primary-color);
  color: var(--primary-color);
  border-radius: 50px;
  padding: 10px 30px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-load-more:hover {
  background-color: var(--primary-color);
  color: var(--white);
}

.instagram-cta {
  margin-top: 60px;
  text-align: center;
  padding: 40px;
  background-color: var(--white);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.instagram-cta h3 {
  font-size: 1.5rem;
  color: var(--primary-color);
  margin-bottom: 10px;
}

.instagram-cta p {
  margin-bottom: 20px;
  color: var(--text-muted);
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  padding: 10px 30px;
  border-radius: 50px;
  font-weight: 500;
  transition: all 0.3s;
}

.btn-primary {
  background-color: var(--primary-color);
  color: var(--white);
}

.btn-primary:hover {
  background-color: var(--primary-dark);
}

.btn.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Lightbox Styles */
.lightbox {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: 0;
  animation: fadeIn 0.3s ease forwards;
}

@keyframes fadeIn {
  to { opacity: 1; }
}

.lightbox-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background-color: var(--white);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 30px rgba(0, 0, 0, 0.3);
  animation: zoomIn 0.3s ease;
}

@keyframes zoomIn {
  from { transform: scale(0.95); }
  to { transform: scale(1); }
}

.lightbox-close {
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: background-color 0.3s;
}

.lightbox-close:hover {
  background-color: rgba(0, 0, 0, 0.8);
}

.lightbox-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: background-color 0.3s;
}

.lightbox-prev {
  left: 15px;
}

.lightbox-next {
  right: 15px;
}

.lightbox-nav:hover {
  background-color: rgba(0, 0, 0, 0.8);
}

.lightbox-image-container {
  position: relative;
  width: 80vw;
  height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #000;
  overflow: hidden;
  cursor: zoom-in;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.lightbox-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: transform 0.2s ease-out;
  transform-origin: center center;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  will-change: transform;
  pointer-events: none;
}

.lightbox-loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
}

.lightbox-caption {
  padding: 15px 20px;
  background-color: var(--white);
}

.lightbox-caption h3 {
  margin: 0 0 5px 0;
  font-size: 1.2rem;
  color: var(--text-color);
}

.lightbox-caption p {
  margin: 0;
  font-size: 0.9rem;
  color: var(--text-muted);
}

/* Responsive */
@media (max-width: 768px) {
  .lightbox-image-container {
    width: 95vw;
    height: 60vh;
  }
  
  .lightbox-nav {
    width: 40px;
    height: 40px;
  }
  
  .photo-item {
    min-height: 300px;
  }
}

@media (max-width: 576px) {
  .photos-grid {
    grid-template-columns: 1fr;
  }
  
  .lightbox-image-container {
    height: 50vh;
  }
}
</style>